import {
  api_admin_super_pin_path,
  api_admin_toggle_pinned_until_path,
  api_admin_update_forum_post_path,
  api_create_forum_post_path,
  api_forum_post_path,
  api_forum_posts_path,
  api_update_forum_post_path,
} from '../../routes';
import { Comment } from './comments';
import request from './request';

export const FORUM_POSTS_KEY = 'FORUM_POSTS_KEY';
export const USER_FORUM_POSTS_KEY = 'USER_FORUM_POSTS_KEY';

export type Entity = {
  label: string;
  value: number;
};

export type ForumPost = {
  id?: number;
  created_at: string;
  body?: string;
  title: string;
  external_url: string;
  comment: Comment;
  manual_entities: Entity[];
  total_comments_count: number;
  pinned_until?: string | null;
  email_digest_enabled: boolean;
  super_pinned: boolean;
  job_tag?: boolean;
  product_tag?: boolean;
  funding_tag?: boolean;
  is_anonymous?: boolean;
};

export interface Pagination {
  hasNextPage: boolean;
  nextPage: number;
  page: number;
  totalCount: number;
  totalPages: number;
}

// TODO: convert these params to camelCase
export interface ForumPostsResult extends Pagination {
  forum_posts: ForumPost[];
  top_entities: TopEntity[];
  // including these because we share them with searching the directory
  profiles: never;
  currentUserId: never;
}

export type TopEntity = {
  name: string;
  entity_join_count: number;
};

export type NewForumPost = Pick<
  ForumPost,
  | 'id'
  | 'title'
  | 'body'
  | 'external_url'
  | 'manual_entities'
  | 'email_digest_enabled'
  | 'job_tag'
  | 'funding_tag'
  | 'product_tag'
  | 'is_anonymous'
>;

export type ForumPostSuperPin = {
  id: number;
  included_super_pin_recipients: string;
  super_pin_preamble: string;
};

export type ForumPostOptions = {
  page?: number;
  perPage?: number;
  order?: string;
  userId?: number;
  topic?: string;
  onSearchPage?: boolean;
};

export async function createForumPost(values: NewForumPost): Promise<ForumPost> {
  const resp = await request('post', api_create_forum_post_path(), { forum_post: values });
  return resp;
}

export async function updateForumPost(values: NewForumPost): Promise<ForumPost> {
  const resp = await request('post', api_update_forum_post_path(values.id), {
    forum_post: values,
  });
  return resp;
}

export async function adminUpdateForumPost(values: NewForumPost): Promise<ForumPost> {
  const resp = await request('post', api_admin_update_forum_post_path(values.id), {
    forum_post: values,
  });
  return resp;
}

export async function adminToggleForumPostPinned(id: number): Promise<ForumPost> {
  const resp = await request('post', api_admin_toggle_pinned_until_path(id));
  return resp;
}

export async function adminSuperPin(values: ForumPostSuperPin): Promise<ForumPost> {
  const resp = await request('post', api_admin_super_pin_path(values.id), {
    forum_post: values,
  });
  return resp;
}

export async function getForumPosts({
  page = 1,
  perPage = 50,
  order,
  userId,
  topic,
  onSearchPage = false,
}: ForumPostOptions): Promise<ForumPostsResult> {
  const resp = await request('get', api_forum_posts_path({ user_id: userId }), {
    on_search_page: onSearchPage,
    order,
    page,
    per_page: perPage,
    topic,
  });
  return resp;
}

export async function getForumPost(id: number | string): Promise<ForumPost> {
  const resp = await request('get', api_forum_post_path(id));
  return resp;
}

export async function deleteForumPost(id: number | string): Promise<Record<string, never>> {
  const resp = await request('delete', api_forum_post_path(id));
  return resp;
}
